// COP — DoD/IC-compliant classification banner-line builder.
// CONTRACT:I2-SECURITY-LABELS.1.0 (the marking vocabulary it formats) /
//          C4-DESIGN-TOKENS.1.0 (the banner color + text tiers).
//
// Builds the OVERALL (highest / "high-water") classification BANNER LINE for the
// displayed content. Per DoD/IC marking standards the banner line is a single,
// properly-ordered marking string that appears IDENTICALLY at the top AND bottom
// of a screen and reflects the overall classification of everything shown.
//
// STANDARD / SOURCES (confirmed via the Authorized Classification and Control
// Markings Register a.k.a. the "CAPCO Register" / IC Markings System Register,
// DoDM 5200.01 Vol 2 Enclosure 4, and the CDSE "Marking Syntax for U.S.
// Classified Information" job aid):
//
//   Banner-line segment order (separated by "//"):
//     CLASSIFICATION // SCI // SAP // AEA // FGI // DISSEMINATION CONTROLS // OTHER (e.g. CUI)
//
//   - Segment separator is the DOUBLE slash "//".
//   - Within a segment, sub-compartments are separated by a SINGLE slash "/"
//     (e.g. "SI-G/TK"); REL-TO country codes within the dissem segment are
//     comma-separated by convention ("REL TO USA, GBR").
//   - Classification is spelled out in FULL CAPS: UNCLASSIFIED, CONFIDENTIAL,
//     SECRET, TOP SECRET. UNCLASSIFIED with no controls is just "UNCLASSIFIED".
//   - Dissemination controls have their own canonical order, e.g. RSEN, ORCON, IMCON,
//     NOFORN, PROPIN, RELIDO, FOUO, LIMDIS, DISPLAY ONLY, FISA, then RELEASABLE TO.
//     AEA (RD/FRD/CNWDI/DCNI) and FGI segments precede dissemination; CUI categories
//     (SP-CTI/…) trail in the OTHER segment (Fuller CAPCO — ARC 1).
//   - NOFORN and RELEASABLE TO are MUTUALLY EXCLUSIVE. At an aggregate high-water, if
//     any item is NOFORN the overall marking is NOFORN (NOFORN dominates / takes
//     precedence; RELEASABLE TO is dropped). RELEASABLE TO is shown only when nothing
//     is NOFORN.
//   - The releasability control is SPELLED OUT as "RELEASABLE TO" in the banner line
//     (the long form CAPCO permits for a banner; never the "REL:" or bare "REL TO"
//     short forms in our display). It lists "USA" FIRST, then other trigraphs in
//     ascending alphabetic order, then tetragraphs (coalitions, e.g. FVEY/NATO/ISAF)
//     in ascending alphabetic order: "RELEASABLE TO USA, GBR" /
//     "SECRET//RELEASABLE TO USA, GBR, FVEY".

import type { HighWater } from "../host/cop.ts";
import { bannerLine as schemaBannerLine } from "../../../../packages/tdflite-classification-schema/src/index.ts";
import type { SecurityLabels } from "../../../../packages/schema/ts/envelope.ts";

/** Standard US marking-banner background colors keyed by canonical classification.
 *  TS_SCI is the design-system canon CAP tier: gold #FFD700 (the highest, most
 *  salient banner), distinct from the plain TOP SECRET orange. */
const CLASS_COLOR: Record<string, string> = {
  UNCLASSIFIED: "#007a33", // green
  CUI: "#502b85", // purple (CUI banner per ISOO CUI marking guidance)
  CONFIDENTIAL: "#0033a0", // blue
  SECRET: "#c8102e", // red
  TOP_SECRET: "#ff8c00", // orange
  TS_SCI: "#FFD700", // gold — TS//SCI CAP tier (tak-design-system canon)
};

/** Banner TEXT (foreground) color keyed by canonical classification. Most tiers
 *  use white on a saturated background; the TS//SCI gold tier needs near-black
 *  text (#1a1400) for legible contrast on gold (tak-design-system canon). */
const CLASS_TEXT_COLOR: Record<string, string> = {
  TS_SCI: "#1a1400", // near-black on gold
};

/** Default banner text color when a tier has no bespoke override (white reads on
 *  every saturated banner background except the gold TS//SCI tier). */
const DEFAULT_BANNER_TEXT_COLOR = "#ffffff";

/** Full-caps spelled-out classification label keyed by canonical classification. */
const CLASS_LABEL: Record<string, string> = {
  UNCLASSIFIED: "UNCLASSIFIED",
  CUI: "CUI",
  CONFIDENTIAL: "CONFIDENTIAL",
  SECRET: "SECRET",
  TOP_SECRET: "TOP SECRET",
  TS_SCI: "TOP SECRET//SI",
};

/** Canonical SCI markings — the `sci_control_system` (SI/TK/HCS) + `sci_compartment`
 *  (SI-G/TK-RUFF/SI-GAMMA/ECI) vocab from packages/abac-pdp/src/policy.ts. The banner SCI
 *  segment is a STRICT WHITELIST of these: a program/operation NICKNAME ("STEEL_HORIZON",
 *  "HIGH_MAST") or a chat room NAME ("ALL CHAT ROOMS") is NOT an SCI marking and must never
 *  appear in the CAPCO classification line. Keep in sync if the policy SCI vocab changes.
 *  (CONTRACT:I2-SECURITY-LABELS.1.0) */
const SCI_SET = new Set(["SI", "TK", "HCS", "SI-G", "TK-RUFF", "SI-GAMMA", "ECI"]);

/** Normalize a raw marking token for CAPCO routing: trim, strip a non-CAPCO field-name
 *  PREFIX some datasets prepend ("SCI:SI" → "SI", "COMPARTMENT:SI-G" → "SI-G", "SAP:BRAVO" →
 *  "BRAVO"), upper-case. The banner shows the bare marking, never the field-name label. */
function normMarking(raw: string): string {
  return raw
    .trim()
    .replace(/^(?:SCI|SCI[_-]?CONTROL|COMPARTMENTS?|SAP|CONTROL)\s*:\s*/i, "")
    .trim()
    .toUpperCase();
}

/** True iff the (normalized) token is a recognized SCI compartment/control marking. */
const isSci = (t: string): boolean => SCI_SET.has(normMarking(t));

/** The SPELLED-OUT releasability dissemination-control prefix CAPCO permits in a
 *  banner line. We deliberately use the long form "RELEASABLE TO" — never the "REL:"
 *  or bare "REL TO" short forms — so the banner reads in plain compliant English. */
export const RELEASABLE_TO_PREFIX = "RELEASABLE TO";

/** Classified-only dissemination controls (ICD 710 / DoDM 5200.01 Vol 2): authorized only
 *  for CLASSIFIED national intelligence, so they must NEVER render on a plain UNCLASSIFIED
 *  banner (e.g. "UNCLASSIFIED//NOFORN" is non-compliant). The PDP already fail-closes such a
 *  contradiction upstream (abac-pdp `validate.ts` R4b); this is the banner's defense-in-depth
 *  display guard. MUST mirror abac-pdp `CLASSIFIED_ONLY_DISSEM` (a drift test asserts equality).
 *  PROPIN/FOUO are intentionally excluded — they are not classified-only. */
export const CLASSIFIED_ONLY_DISSEM = new Set(["NOFORN", "ORCON", "IMCON", "RELIDO"]);

/** Standard marking BACKGROUND color for a (canonical) classification.
 *  TOP SECRET is orange; TS//SCI (key "TS_SCI") is the canon gold #FFD700 CAP
 *  tier. Exposed so callers can colorize a banner. */
export function bannerColor(classification: string): string {
  return CLASS_COLOR[classification] ?? "#3a3a3a";
}

/** Marking TEXT (foreground) color for a (canonical) classification — the
 *  legible-contrast partner to bannerColor. Defaults to white; the TS//SCI gold
 *  tier returns near-black #1a1400 so the marking reads on gold. */
export function bannerTextColor(classification: string): string {
  return CLASS_TEXT_COLOR[classification] ?? DEFAULT_BANNER_TEXT_COLOR;
}

/** The BANNER TIER (for COLOR/TEXT selection) derived from a full marking aggregate.
 *  Elevates a TOP SECRET banner that carries ANY SCI compartment to the canon
 *  TS//SCI GOLD tier (#FFD700 on near-black #1a1400) — the highest, most-salient
 *  banner (tak-design-system frame 06, the cleared commander's full picture).
 *
 *  DISPLAY-ONLY: it returns a COLOR key, never a stored classification, and never
 *  touches the PDP/clearance decision. The marking STRING is unaffected — bannerLine
 *  still derives "TOP SECRET" from hw.classification and appends "//SI" from
 *  hw.missions, so colorizing via this tier avoids any double-"//SI". */
export function bannerTier(hw: HighWater): string {
  // Gold TS//SCI tier elevates ONLY on a true SCI compartment marking — not on an AEA
  // (RD/FRD/…), FGI, or CUI-category token that may also ride the missions stream.
  // Elevate to the gold TS//SCI tier ONLY on a recognized SCI marking (SCI_SET) — never on a
  // program nickname riding the missions stream. SCI can arrive on either stream (some datasets
  // carry a bare compartment in caveats), so check both.
  const hasSci = hw.missions.some(isSci) || hw.caveats.some(isSci);
  return hw.classification === "TOP_SECRET" && hasSci ? "TS_SCI" : hw.classification;
}

/** Spelled-out, full-caps classification label (e.g. "TOP_SECRET" → "TOP SECRET"). */
export function classificationLabel(classification: string): string {
  return CLASS_LABEL[classification] ?? classification;
}

/** CAPCO line labels for the dual banner. The banner SHOWS a CAPCO marking, but a
 *  reader also needs to know WHICH thing is being marked — the level they are working
 *  AT (the Glomar-safe subject side) vs. the level ACTUALLY rendered (the data side).
 *  These are the human-readable prefixes the two rows render.
 *
 *  GLOMAR SAFETY: `HIGHEST_POSSIBLE` labels the QUERY level only (= min(clearance,
 *  NPE, down-filter)). It is the ceiling the SUBJECT may work at, NOT the data
 *  ceiling — a data ceiling would reveal that higher-classification content exists.
 *  Callers MUST feed this the query level, never the as-rendered high-water. */
export const CAPCO_LINE_LABEL = {
  /** ROW 1 — the subject's working ceiling (the down-filterable query level). */
  HIGHEST_POSSIBLE: "Highest Possible Classification",
  /** ROW 2 / bottom — the DoD/IC overall marking of what is ACTUALLY on screen. */
  AS_RENDERED: "Classification",
} as const;

/** Compose a "{label}: {marking}" CAPCO banner caption. Pure string assembly; the
 *  caller decides which label + marking pair is Glomar-appropriate for the row. */
export function capcoCaption(label: string, marking: string): string {
  return `${label}: ${marking}`;
}

/** Build the DoD/IC-compliant overall banner line from a HighWater aggregate, in the
 *  Fuller CAPCO segment order (ARC 1):
 *
 *    CLASSIFICATION // SCI // AEA // FGI // DISSEMINATION (+ RELEASABLE TO) // CUI
 *
 *  DELEGATED (view-as descope foundation, 2026-07-11): the string is now built by the
 *  VENDORED canonical builder — packages/tdflite-classification-schema (TDFLite v0.10.0)
 *  `bannerLine(labels)`, which is itself single-sourced from THIS file's pre-swap body —
 *  through the inverse HighWater → SecurityLabels adapter below. Byte-parity is pinned
 *  two ways: the goldens in dod-marking.test.ts (captured against the pre-swap body) and
 *  the corpus-280 marking_string vectors (12/12 byte-exact through both builders).
 *
 *  The routing semantics are UNCHANGED (they live in the vendored module now): SCI strict
 *  whitelist (nickname-drop), AEA/FGI/CUI own segments, canonical dissem order, the
 *  classified-only-dissem drop on plain UNCLASSIFIED, NOFORN-dominates (drops RELEASABLE
 *  TO), USA-first REL ordering, spelled-out "RELEASABLE TO". The COLOR/tier/caption
 *  helpers in this file stay COP-LOCAL (the vendored CLASS_LABEL deliberately lacks our
 *  presentation-only TS_SCI → "TOP SECRET//SI" spell-out — see classificationLabel). */
export function bannerLine(hw: HighWater): string {
  // Inverse adapter: HighWater (COP aggregate) → SecurityLabels (the canonical envelope
  // facet shape the vendored builder reads). A HighWater always arrives pre-defaulted,
  // but "" is normalized to UNCLASSIFIED to match the vendored builder's own default.
  const labels: SecurityLabels = {
    classification: hw.classification || "UNCLASSIFIED",
    mission: hw.missions,
    caveats: hw.caveats,
    releasability: hw.releasability,
    // ORCON reaches a HighWater as a caveat token; never double-fold via the boolean.
    originator_control: false,
    // Required-by-type filler — SecurityLabels fields bannerLine does not read.
    policy_ref: "",
    payload_mode: "clear",
    leakage_profile: "clear",
  };
  return schemaBannerLine(labels);
}
